// essence-breakdown.ts — client-side render of the creature essence breakdown
// panel, reading from window.FateTide.casterCache (no DB ping on a cache hit).
//
// Replaces the PHP endpoint options/tools/encounter_creatures/encounter_creature_info/
// essence_info_inc.php. Data comes from caster_arr.computed.creature.essences.<name>
// ({total, base, sources, breakdown}); icons via iconMap(); HTML injected into
// #div_refresh_side_info. Pattern mirrors encounter-summary-render.ts.
//
// Improvements over the PHP version:
//   - zero rows are hidden (a category renders only with a non-zero total or real
//     source lines); the old Default row read a retired top-level slot and showed 0
//     for creatures — this reads breakdown.default, so it's now correct.
//   - Other/Override are PC-only (omitted entirely for template/encounter creatures)
//     and read-only (the edit flow is a deferred follow-up — see plan).

import type { CachedCaster, EssenceProvenance, EssenceSourceRow, CapacityEntry } from './types';
import { renderSourceLineBlock, resolveLineKind } from './source-line-block';
import { setActiveInfoPanel, paintActiveInfoPanel } from './active-panel';

// Essence variable_name → {item_id (for the header icon), display name}.
// Stable catalog ids from additions.php Essence::CORE / ::EXTRA.
const ESSENCE_META: Record<string, { id: string; name: string }> = {
    body:    { id: '22918408-67CC-4093-80E8-BF6FA8915C21', name: 'Body' },
    mind:    { id: '63ABC96E-3804-4E1F-B38D-AABA3237B2D8', name: 'Mind' },
    soul:    { id: '72F93730-134F-4CA2-8FC4-F2915F20CAC6', name: 'Soul' },
    spirit:  { id: '86792A8B-2070-4144-9C81-0D85706131DF', name: 'Spirit' },
    fortune: { id: 'E769E451-65EE-3628-1A78-D12C9A3FF02B', name: 'Fortune' },
    luck:    { id: '7B7E8F97-6843-57CF-422C-5930CFA98B06', name: 'Luck' },
};

// Category → row icon (matches the PHP icon_map slugs). Order = render order.
const CATEGORY_ROWS: ReadonlyArray<{ cat: string; icon: string }> = [
    { cat: 'ancestral',  icon: 'ancestry'  },
    { cat: 'background', icon: 'background' },
    { cat: 'feat',       icon: 'feat'      },
    { cat: 'inventory',  icon: 'inventory' },
    { cat: 'ability',    icon: 'ability'   },
];

/** Unwrap a PhpScalar ([v]) or pass a bare scalar through. */
function unwrap(v: unknown): unknown {
    return Array.isArray(v) ? v[0] : v;
}

function num(v: unknown): number {
    const n = Number(unwrap(v));
    return Number.isFinite(n) ? n : 0;
}

/** Body-only lifting-capacity block, read from computed.creature.capacity.<key>.total. */
function renderLiftingCapacity(caster: CachedCaster): string {
    const cap = caster.computed?.creature?.capacity ?? {};
    const total = (key: string): string => {
        const entry = (cap as Record<string, CapacityEntry | number | undefined>)[key];
        if (entry === undefined || entry === null) return '-';
        const t = (typeof entry === 'object') ? entry.total : entry;
        return (t === undefined || t === null) ? '-' : String(t);
    };
    return `
    <div class="text-left p-2">
        <ul class="list-group text-left">
            <li class="list-group-item active" aria-current="true"><strong>Lifting Capacity (lbs):</strong></li>
            <li class="list-group-item">Encumbered Carrying Capacity: <span class="badge bg-warning">${escapeHtml(total('encumbered'))}</span></li>
            <li class="list-group-item">Heavily Encumbered Carrying Capacity: <span class="badge bg-danger">${escapeHtml(total('heavily_encumbered'))}</span></li>
            <li class="list-group-item">Max Carrying Capacity: <span class="badge bg-dark border">${escapeHtml(total('max'))}</span></li>
            <li class="list-group-item">Push, Drag, or Lift: <span class="badge bg-info">${escapeHtml(total('push_drag_lift'))}</span></li>
        </ul>
    </div>`;
}

function essenceProvenance(caster: CachedCaster, essenceName: string): EssenceProvenance | undefined {
    return caster.computed?.creature?.essences?.[essenceName]
        ?? caster.computed?.essences?.[essenceName];
}

export function renderEssenceTable(caster: CachedCaster, essenceName: string): string {
    const meta = ESSENCE_META[essenceName] ?? { id: '', name: essenceName };
    const headerIcon = meta.id !== '' ? iconMap(meta.id) : iconMap(essenceName);
    // Standard tinted section header (table equivalent of list-group-item-primary,
    // matching the essence headers in list_display_essences_inc.php).
    const header = `<thead><tr><th scope="col" colspan="2" class="table-primary align-middle">${headerIcon} ${escapeHtml(meta.name)}</th></tr></thead>`;

    const ess = essenceProvenance(caster, essenceName);
    if (!ess) {
        return `<table class="table border">${header}<tbody><tr><td class="text-center text-muted p-3">No essence data.</td></tr></tbody></table>`;
    }

    const bd = ess.breakdown ?? {};
    const sources: EssenceSourceRow[] = Array.isArray(ess.sources) ? ess.sources : [];
    const byCat: Record<string, EssenceSourceRow[]> = {};
    for (const row of sources) {
        // ROUND-6 #3: `category` is elided at the store point when it equals `kind`;
        // ROUND-9 #5: `kind` is elided when it equals the catalog item_type of href_id.
        const cat = String(row.category ?? resolveLineKind(caster, row));
        (byCat[cat] ??= []).push(row);
    }

    let rows = '';

    // Default (text label, no icon). ALWAYS shown — the essence's own base value is
    // always relevant, so a 0 essence (e.g. Soul) is explicitly marked as 0 rather than
    // hidden. (Only the contributor categories below hide when empty.)
    const def = Number(bd.default ?? 0);
    rows += `<tr><th scope="row" class="align-middle">Default:</th><td class="text-end"><h3 class="mb-0"><span class="badge bg-primary">${def}</span></h3></td></tr>`;

    // Category rows — hidden when both the breakdown total and source lines are empty.
    for (const { cat, icon } of CATEGORY_ROWS) {
        const lines = byCat[cat] ?? [];
        const total = Number(bd[cat] ?? 0);
        if (lines.length === 0 && total === 0) {
            continue;
        }
        const valueCell = lines.length > 0
            ? renderSourceLineBlock(lines, icon, total, 'bg-secondary', caster)
            : `<h3 class="mb-0"><span class="badge bg-secondary">${total}</span></h3>`;
        rows += `<tr><th scope="row" class="align-middle">${iconMap(icon)}:</th><td class="text-center align-middle">${valueCell}</td></tr>`;
    }

    // Other / Override — PC-only, read-only, hidden when 0.
    // Discriminate PC-vs-creature via encounter_creatures.item_type FIRST: an encounter caster blob
    // commonly carries NO top-level `items` bucket (post-reshape item_type rides on encounter_creatures),
    // so an items-only read returns '' for a PLAYER CHARACTER token in an encounter → these rows would be
    // wrongly hidden for a real PC. Mirrors casterItemType() (read-helpers): ec-first, then items fallback,
    // empty ec value falls through. (NB creature-summary's local itemTypeOf reads items-first — either
    // order agrees for real data; the ec-first order here matches the canonical helper.)
    // [[reference_pc_creature_item_type_discriminator]]
    const _ecType = unwrap((caster.encounter_creatures as Record<string, unknown> | undefined)?.item_type);
    const itemType = String(
        (_ecType !== undefined && _ecType !== null && _ecType !== '')
            ? _ecType
            : (unwrap(caster.items?.item_type) ?? ''),
    );
    if (itemType === 'player_character') {
        const other = Number(bd.other ?? 0);
        const override = Number(bd.override ?? 0);
        if (other !== 0) {
            rows += `<tr><th scope="row" class="align-middle">Other</th><td class="text-end"><h3 class="mb-0"><span class="badge bg-secondary">${other}</span></h3></td></tr>`;
        }
        if (override !== 0) {
            rows += `<tr><th scope="row" class="align-middle">Override</th><td class="text-end"><h3 class="mb-0"><span class="badge bg-secondary">${override}</span></h3></td></tr>`;
        }
    }

    const total = Number(ess.total ?? 0);
    let html = `<table class="table border">
        ${header}
        <tbody>${rows}</tbody>
        <tfoot>
            <tr>
                <th scope="row" class="align-middle">${iconMap(essenceName)} Total</th>
                <td class="text-end"><h3 class="mb-0"><span class="badge bg-warning">${total}</span></h3></td>
            </tr>
        </tfoot>
    </table>`;

    if (essenceName === 'body') {
        html += renderLiftingCapacity(caster);
    }

    return html;
}

/**
 * Entry point. Renders the essence breakdown for a caster into #targetId, reading
 * from the client cache (fetch-on-miss). Repointed onclick: the essence icon button.
 */
export async function renderEssenceInfoPanel(
    casterId: string,
    essenceName: string,
    targetId = 'div_refresh_side_info',
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) {
        return;
    }

    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-muted">Loading…</div>';
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }

    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-danger">Could not load creature data.</div>';
        return;
    }

    const render = (c: CachedCaster) => renderEssenceTable(c, essenceName);
    setActiveInfoPanel(casterId, targetId, render);
    paintActiveInfoPanel(caster);
}
